import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import {
  fetchDoctorConsultationState,
  holdConsultation,
  resumeConsultation,
  startEmergencyConsultation,
} from './api';
import type { QueueItem } from './types';

/**
 * The three explicit consultation-state actions — hold, resume, and the
 * emergency interruption — plus a read of what a doctor is currently doing.
 *
 * Deliberately NOT optimistic, unlike useUpdateQueueStatus. Each of these
 * either moves two rows at once (the emergency one holds someone else's
 * consultation) or is refused outright by a server rule the client cannot
 * evaluate, so guessing the outcome and rolling back would flicker the queue
 * through a state that never existed. They invalidate instead, and the 30s
 * refetch in useTodayQueue picks up anything a second user did.
 */
function useQueueRefresh() {
  const queryClient = useQueryClient();
  return (updated?: QueueItem) => {
    if (updated) {
      queryClient.setQueriesData<QueueItem[]>({ queryKey: keys.queue.today() }, (old) =>
        old?.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
      );
    }
    // The other row (the one just held, or the one this resume displaced)
    // is only known to the server, so a refetch is the only honest refresh.
    queryClient.invalidateQueries({ queryKey: keys.queue.today() });
  };
}

/** "Hold & Attend Emergency" — holds the doctor's current patient and starts this emergency one. */
export function useStartEmergencyConsultation() {
  const refresh = useQueueRefresh();
  return useMutation({
    mutationFn: (id: number) => startEmergencyConsultation(id),
    onSuccess: refresh,
  });
}

/** "Resume Consultation" — back to the same patient, same context. */
export function useResumeConsultation() {
  const refresh = useQueueRefresh();
  return useMutation({
    mutationFn: (id: number) => resumeConsultation(id),
    onSuccess: refresh,
  });
}

/** Pause the consultation in progress without starting anything else. */
export function useHoldConsultation() {
  const refresh = useQueueRefresh();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => holdConsultation(id, reason),
    onSuccess: refresh,
  });
}

/**
 * Pre-flight for the Queue stage's "Continue": lets the screen say "Dr Kumar
 * is with another patient" *before* navigating, instead of bouncing off the
 * backend's 409 after the Consultation page has already opened. Advisory
 * only — the backend re-checks under a doctor lock and remains the source of
 * truth, so a stale answer here can never let a second consultation through.
 */
export function useDoctorConsultationState(doctorId: number | null | undefined) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.queue.today(), 'doctor-consultation-state', branchVersion, doctorId ?? null],
    queryFn: () => fetchDoctorConsultationState(doctorId as number),
    enabled: !!doctorId,
    staleTime: 5_000,
  });
}
